import { ContactModel, ContactRepository, LoggerHelper } from '@src/core';
import { useProjectContext } from '@src/modules';
import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { lru } from 'tiny-lru';

const CacheContext = createContext<any>(null);

const CacheProvider: FC<PropsWithChildren> = ({ children }) => {
  const { projectId, firestorePath } = useProjectContext();

  const [contacts, setContacts] = useState<ContactModel[]>([]);
  const [cacheVersion, setCacheVersion] = useState(0);

  const contactCache = useMemo(
    () =>
      lru<ContactModel>(
        1000, // max items
        1000 * 60 * 5 // TTL: 5 minutos
      ),
    []
  );

  const contactRefresh = () => {
    if (!firestorePath) return;

    const fetchData = () =>
      new Promise(resolve => {
        resolve(ContactRepository.getList<ContactModel>(projectId));
      });

    fetchData()
      .then((data: any) => {
        setContacts(data);
      })
      .catch((error: any) => LoggerHelper.error('Falha ao carregar o cache de participantes', error));
  };

  useEffect(() => {
    contactRefresh();
  }, [firestorePath, projectId]);

  useEffect(() => {
    contactRefresh();
  }, []);

  useEffect(() => {
    contacts.forEach(contact => {
      const cacheKey = `${projectId}-${contact.id}`;
      contactCache.set(cacheKey, contact);
    });

    setCacheVersion(v => v + 1);
  }, [contacts, projectId, contactCache]);

  const getContact = (contactId: number): ContactModel | null => {
    const cacheKey = `${projectId}-${contactId}`;

    const cached = contactCache.get(cacheKey);
    if (cached) return cached;

    const fromList = contacts.find(c => c.id === contactId);
    if (fromList) {
      contactCache.set(cacheKey, fromList);
      return fromList;
    }

    if (firestorePath) {
      ContactRepository.getById(contactId, projectId).then((data: ContactModel | null) => {
        if (data !== null) {
          contactCache.set(cacheKey, data);
          // Força re-render para atualizar componentes
          setCacheVersion(v => v + 1);
        }
      });
    }

    return null;
  };

  return (
    <CacheContext.Provider
      value={{
        getContact,
        contacts,
        contactRefresh,
        cacheVersion, // Permite componentes re-renderizarem quando cache atualiza
      }}
    >
      {children}
    </CacheContext.Provider>
  );
};

interface CacheContextProps {
  getContact: (contactId: number) => ContactModel | null;
  contacts: ContactModel[];
  contactRefresh: () => void;
  cacheVersion: number;
}

const useCacheContext = (): CacheContextProps => useContext(CacheContext);

export { CacheProvider, useCacheContext };
